import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { clearAuthenticatedClientState } from '@/lib/authSession';
import { assertServerAuthRateLimit } from '@/lib/authRateLimit';
import { checkRateLimit, getRateLimitReset } from '@/lib/rate-limit';
import { recordSecurityEvent } from '@/lib/securityEvents';
import { elevateMfaSession, requiresMfaElevation } from '@/lib/mfaAssurance';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';

export function useAuth() {
  const { user, session, isLoading, isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const signUp = useMutation({
    mutationFn: async ({
      email,
      password,
      fullName,
      emailRedirectTo,
      inviteToken,
      ownerSignupToken,
    }: {
      email: string;
      password: string;
      fullName: string;
      emailRedirectTo?: string;
      inviteToken?: string;
      ownerSignupToken?: string;
    }) => {
      if (!checkRateLimit('auth:signUp', 5, 60_000)) {
        const secs = getRateLimitReset('auth:signUp', 60_000);
        throw new Error(`Too many sign-up attempts. Try again in ${secs}s.`);
      }
      await assertServerAuthRateLimit('signUp', email);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            ...(inviteToken ? { invite_token: inviteToken } : {}),
            ...(ownerSignupToken ? { owner_signup_token: ownerSignupToken } : {}),
          },
          emailRedirectTo: emailRedirectTo ?? `${window.location.origin}/confirm-email`,
        },
      });
      if (error) throw error;
      return data;
    },
  });

  const signIn = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      if (!checkRateLimit('auth:signIn', 5, 60_000)) {
        const secs = getRateLimitReset('auth:signIn', 60_000);
        throw new Error(`Too many sign-in attempts. Try again in ${secs}s.`);
      }
      await assertServerAuthRateLimit('signIn', email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return data;
    },
  });

  const signOut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) throw error;
    },
    onSuccess: () => {
      clearAuthenticatedClientState(queryClient);
    },
  });

  const signOutOtherDevices = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut({ scope: 'others' });
      if (error) throw error;
    },
  });

  const signOutEverywhere = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
    },
    onSuccess: () => {
      clearAuthenticatedClientState(queryClient);
    },
  });

  const sendPasswordReset = useMutation({
    mutationFn: async (input: string | { email: string; redirectTo?: string }) => {
      const email = typeof input === 'string' ? input : input.email;
      const redirectTo = typeof input === 'string' ? undefined : input.redirectTo;
      if (!checkRateLimit('auth:resetPassword', 3, 60_000)) {
        const secs = getRateLimitReset('auth:resetPassword', 60_000);
        throw new Error(`Too many reset attempts. Try again in ${secs}s.`);
      }
      await assertServerAuthRateLimit('resetPassword', email);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectTo ?? `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    },
  });

  const updatePassword = useMutation({
    mutationFn: async (input: string | { newPassword: string; currentPassword?: string; mfaCode?: string }) => {
      const password = typeof input === 'string' ? input : input.newPassword;

      if (typeof input !== 'string' && input.currentPassword) {
        if (!user?.email) {
          throw new Error('Your current session is missing an email address.');
        }

        if (!checkRateLimit('auth:updatePassword', 5, 60_000)) {
          const secs = getRateLimitReset('auth:updatePassword', 60_000);
          throw new Error(`Too many password attempts. Try again in ${secs}s.`);
        }

        const { error: verificationError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: input.currentPassword,
        });

        if (verificationError) {
          throw new Error('Current password is incorrect.');
        }
      }

      if (await requiresMfaElevation()) {
        await elevateMfaSession(typeof input === 'string' ? '' : input.mfaCode ?? '');
      }

      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      void recordSecurityEvent({
        organizationId: useOrgStore.getState().organization?.id,
        action: 'PASSWORD_CHANGED',
        subjectUserId: user?.id,
      });
    },
  });

  const resendConfirmation = useMutation({
    mutationFn: async (input: string | { email: string; emailRedirectTo?: string }) => {
      const email = typeof input === 'string' ? input : input.email;
      const emailRedirectTo = typeof input === 'string' ? undefined : input.emailRedirectTo;
      if (!checkRateLimit('auth:resendConfirmation', 3, 60_000)) {
        const secs = getRateLimitReset('auth:resendConfirmation', 60_000);
        throw new Error(`Too many confirmation email attempts. Try again in ${secs}s.`);
      }
      await assertServerAuthRateLimit('resendConfirmation', email);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: emailRedirectTo ?? `${window.location.origin}/confirm-email`,
        },
      });
      if (error) throw error;
    },
  });

  return {
    user,
    session,
    isLoading,
    isAuthenticated,
    signUp,
    signIn,
    signOut,
    signOutOtherDevices,
    signOutEverywhere,
    sendPasswordReset,
    updatePassword,
    resendConfirmation,
  };
}
